import { useIntl } from 'react-intl';
import { Divider, Form, Modal, Skeleton } from 'antd';
import { PageProps } from '@/types/PageProps';
import { SaveButton } from '@/components/_common/Buttons';
import AccountReceivableForm from '@/components/Registry/BillToReceive/AccountReceivableForm';
import {
  AccountReceivableDebtor,
  AccountReceivableDetail,
  AccountReceivableFormValues,
  AccountReceivableSituation,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import {
  useAccountReceivableDetail,
  useAccountReceivableEdit,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory, useRouteMatch } from 'react-router-dom';
import AccountReceivableFooter from '@/components/Registry/BillToReceive/AccountReceivableFooter';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import handleFormChange from '@/hooks/handleFormChange';
import { useBankAccountMutation } from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { DefaultRouteTabs } from '@/components/_common';
import { UseQueryResult } from 'react-query';

interface AccountReceivableContextParams {
  accountReceivableHook: UseQueryResult<AccountReceivableDetail, unknown>;
  accountReceivable?: AccountReceivableDetail;
  nextInstallment: VoidFunction;
  prevInstallment: VoidFunction;
  currentInstallment: number;
  totalInstallments: number;
  hasUpdateTariff: boolean;
  setUpdateTariff: (updated: boolean) => void;
}

const AccountReceivableContext = createContext(
  {} as AccountReceivableContextParams,
);

export const useAccountReceivableContext = () =>
  useContext(AccountReceivableContext);

function ReleasesBillToReceiveDetailPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<AccountReceivableFormValues>();
  const [hasUpdateTariff, setUpdateTariff] = useState(false);

  const { releaseId, installmentId } = useRouteMatch<{
    releaseId: string;
    installmentId: string;
  }>().params;

  const [currentInstallment, setCurrentInstallment] = useState<number>(0);
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const accountReceivableEdit = useAccountReceivableEdit();
  const bankAccountMutation = useBankAccountMutation();

  const accountReceivable = useAccountReceivableDetail(Number(releaseId));

  const totalInstallments =
    accountReceivable.data?.installmentReceivables.length || 0;

  const accountInstallment =
    accountReceivable.data?.installmentReceivables[currentInstallment];

  useEffect(() => {
    if (installmentId && accountReceivable?.data) {
      setCurrentInstallment(() =>
        accountReceivable.data.installmentReceivables.findIndex(
          (item) => item?.id === Number(installmentId),
        ),
      );
    }
  }, [installmentId, accountReceivable?.data, accountReceivable.dataUpdatedAt]);

  useEffect(() => {
    if (accountReceivable?.data && typeof currentInstallment === 'number') {
      const installmentReceivable =
        accountReceivable.data?.installmentReceivables[currentInstallment];

      if (installmentReceivable?.bankAccountId) {
        bankAccountMutation.mutateAsync(installmentReceivable.bankAccountId);
      } else {
        bankAccountMutation.reset();
      }

      form.setFieldsValue({
        installmentId: installmentReceivable?.id,
        companyId: accountReceivable.data?.companyId,
        costCenterId: accountReceivable.data?.costCenterId,
        authorizationNumber: accountReceivable.data?.authorizationNumber,
        debtor: accountReceivable.data?.customerId
          ? AccountReceivableDebtor.Costumer
          : AccountReceivableDebtor.Insurer,
        customerId: accountReceivable.data?.customerId,
        insuerId: accountReceivable.data?.insurerId,
        planAccountId: accountReceivable.data?.planAccountId,
        installmentNumber: installmentReceivable?.installmentNumber,
        bankAccountId: installmentReceivable?.bankAccountId,
        observation: installmentReceivable?.observation,
        dueDate: installmentReceivable?.dueDate
          ? moment(installmentReceivable?.dueDate)
          : undefined,
        emissionDate: installmentReceivable?.emissionDate
          ? moment(installmentReceivable?.emissionDate)
          : undefined,
        payDay: installmentReceivable?.payDate
          ? moment(installmentReceivable?.payDate)
          : undefined,
        description: installmentReceivable?.description,
        documentTypeId: installmentReceivable?.documentTypeId,
        documentNumber: installmentReceivable?.documentNumber,
        rateValue: installmentReceivable?.rateValue,
        installmentValue: installmentReceivable?.installmentValue || 0,
        discountIncrease: installmentReceivable?.discountIncrease || 0,
        netValue:
          (installmentReceivable?.installmentValue || 0) +
          (installmentReceivable?.discountIncrease || 0) -
          (installmentReceivable?.rateValue || 0),
        amountReceivable:
          (installmentReceivable?.installmentValue || 0) +
          (installmentReceivable?.discountIncrease || 0),
        amountAdjusted: installmentReceivable?.amountAdjusted,
      });
    }
  }, [accountReceivable.dataUpdatedAt, currentInstallment]);

  function goToPage(id?: number) {
    const paths = window.location.pathname
      .split('/')
      .filter((path) => !!path)
      .slice(0, -2);

    history.push(`/${paths.join('/')}/${id}/general`);
  }

  function nextInstallment() {
    const nextData =
      accountReceivable.data?.installmentReceivables[currentInstallment + 1];

    if (nextData) {
      goToPage(nextData.id);
    }
  }

  function prevInstallment() {
    const prevData =
      accountReceivable.data?.installmentReceivables[currentInstallment - 1];

    if (prevData) {
      goToPage(prevData.id);
    }
  }

  async function onSubmit() {
    try {
      if (accountReceivable.data?.id && accountInstallment) {
        const values = await form.validateFields();

        await accountReceivableEdit.mutateAsync({
          ...values,
          currentSituation: accountInstallment?.situation,
          accountReceivableId: accountReceivable.data.id,
          installmentId: values.installmentId,
          dueDate: values?.dueDate ? values.dueDate.format() : undefined,
          emissionDate: values?.emissionDate
            ? values.emissionDate.format()
            : undefined,
          payDay: values?.payDay ? values.payDay.format() : undefined,
        });

        setFormChange({});
      }
    } catch (error) {
    } finally {
      setUpdateTariff(false);
    }
  }

  const confirmModal = () => {
    Modal.confirm({
      title: 'Deseja realmente atualizar a tarifa?',
      content:
        'Ao confirmar, o percentual da tarifa será atualizado de acordo com a tarifa salva no tipo de documento e não será mais possível reverter esta operação',
      onOk: onSubmit,
      okText: 'Atualizar',
    });
  };

  return (
    <>
      <AccountReceivableContext.Provider
        value={{
          accountReceivableHook: accountReceivable,
          accountReceivable: accountReceivable.data,
          nextInstallment,
          prevInstallment,
          currentInstallment: currentInstallment || 0,
          totalInstallments,
          hasUpdateTariff,
          setUpdateTariff,
        }}
      >
        <DefaultRouteTabs
          permissions={route.permissions}
          route={{ ...route.parent, key: route.key }}
          baseKey={installmentId.toString()}
          basePath={window.location.pathname.replace('/general', '')}
          extra={
            <SpaceForButton>
              <SaveButton
                block
                type="primary"
                onClick={hasUpdateTariff ? confirmModal : onSubmit}
                loading={accountReceivableEdit.isLoading}
                disabled={
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT,
                  ]) ||
                  accountInstallment?.situation ===
                    AccountReceivableSituation.settled ||
                  accountInstallment?.situation ===
                    AccountReceivableSituation.canceled
                }
              >
                {intl.formatMessage({ id: 'commons.form.button.save' })}
              </SaveButton>
            </SpaceForButton>
          }
        >
          <div style={{ position: 'relative' }}>
            <Skeleton
              loading={
                accountReceivable.isLoading && bankAccountMutation.isLoading
              }
            >
              <Form.Provider
                onFormChange={(name, info) =>
                  handleFormChange(name, info, setFormChange)
                }
              >
                <AccountReceivableForm
                  form={form}
                  isEdit
                  disabled={
                    accountInstallment?.situation !==
                    AccountReceivableSituation.open
                  }
                />
              </Form.Provider>

              <Divider />

              <AccountReceivableFooter
                disabledBackButton={disabled}
                bankAccount={bankAccountMutation.data}
                handleSave={onSubmit}
              />
            </Skeleton>
          </div>
        </DefaultRouteTabs>
      </AccountReceivableContext.Provider>
    </>
  );
}

export default ReleasesBillToReceiveDetailPage;
